import { isDefined } from "@clipboard-health/util-ts";
import type Knock from "@knocklabs/client";
import { useKnockClient } from "@knocklabs/react";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useCallback } from "react";

import {
  KNOCK_LAST_REVIEW_PROMPT_SHOWN_AT_ATTRIBUTE,
  KNOCK_REVIEW_PROMPT_ACTION_TYPE_ATTRIBUTE,
  KNOCK_REVIEW_PROMPT_DISMISS_COUNT_ATTRIBUTE,
  REVIEW_PROMPT_ACTION_TYPES,
  type ReviewPromptActionType,
} from "./constants";

/**
 * Stamps the rating-prompt cooldown pointer for a terminal mood-step action
 * (No → "decline", Ask me later → "dismiss"). Writes `lastReviewPromptShownAt`
 * = now and `lastReviewPromptActionType` in a single identify; the dashboard
 * gates the per-type re-prompt window off that pair. The dismiss path
 * additionally reads `reviewPromptDismissCount` and folds the increment into
 * that same identify, so dashboard targeting can cap serial deferrals. Decline
 * stays a pure write with no preceding read — the windows live in dashboard
 * targeting, not in code — and the rate path stamps the same pointer from
 * `useRequestInAppReview` alongside the native request.
 */
export function useRecordReviewPromptCooldown(): (
  actionType: ReviewPromptActionType
) => Promise<void> {
  const knockClient = useKnockClient();

  return useCallback(
    async (actionType: ReviewPromptActionType) => {
      const dismissCount =
        actionType === REVIEW_PROMPT_ACTION_TYPES.dismiss
          ? await readIncrementedDismissCount({ knockClient })
          : undefined;

      try {
        await knockClient.user.identify({
          [KNOCK_LAST_REVIEW_PROMPT_SHOWN_AT_ATTRIBUTE]: new Date().toISOString(),
          [KNOCK_REVIEW_PROMPT_ACTION_TYPE_ATTRIBUTE]: actionType,
          ...(isDefined(dismissCount)
            ? { [KNOCK_REVIEW_PROMPT_DISMISS_COUNT_ATTRIBUTE]: dismissCount }
            : {}),
        });
      } catch (error) {
        logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
      }
    },
    [knockClient]
  );
}

interface ReadIncrementedDismissCountParams {
  knockClient: Knock;
}

/**
 * Reads the stored dismiss count and returns the incremented count to write: a
 * valid non-negative number increments (floored); anything else resets to 1 —
 * this hook is the field's only writer, so an invalid value (null, a string, a
 * negative) carries no real count and would otherwise strand the user in a
 * state the dashboard rule can't classify. A failed read returns undefined and
 * the caller omits the field: the stored state is unknown, identify merges the
 * fields included in a request, and a fallback write could overwrite a real
 * higher count and keep that user under the cap forever.
 */
async function readIncrementedDismissCount(
  params: ReadIncrementedDismissCountParams
): Promise<number | undefined> {
  const { knockClient } = params;

  let storedCount: unknown;
  try {
    const user = await knockClient.user.get();
    storedCount = user[KNOCK_REVIEW_PROMPT_DISMISS_COUNT_ATTRIBUTE];
  } catch (error) {
    logError(APP_V2_APP_EVENTS.RATING_PROMPT_KNOCK_IDENTIFY_FAILED, { error });
    return undefined;
  }

  return typeof storedCount === "number" && Number.isFinite(storedCount) && storedCount >= 0
    ? Math.floor(storedCount) + 1
    : 1;
}
